import React, { useEffect, useMemo, useState } from "react";
import { FaUsers, FaTimes, FaEdit, FaTrashAlt, FaFilter, FaSearch, FaInfoCircle, FaMicrophone } from "react-icons/fa";
import CommonLayout from "../../../layouts/CommonLayout";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { getAuthConfig } from "../../../services/apiUtils";
import { ROUTE_CONSTANT } from "../../../routes/constant";
import { toast } from "react-toastify";
import { allAPiServicesCall } from "../../../services/apiServices";
import { artistImageUrl } from "../../../utils/utils";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import "./ArtistsList.css";

const PAGE_SIZE = 50;

// Simple debounce
function useDebouncedValue(value, delay = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function ArtistsList() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // ✅ read from URL (with defaults)
  const page = Number(params.get("page") || 1);
  const sortParam = params.get("sort") || "-artist_id"; // newest first
  const qParam = params.get("q") || "";
  const searchFieldParam = params.get("searchField") || "artist_name";

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 1,
  });

  // Search field options
  const searchFieldOptions = [
    { value: "artist_name", label: "Name" },
    { value: "artist_id", label: "ID" },
  ];

  // local search box state, debounced to reduce API calls
  const [search, setSearch] = useState(qParam);
  const [searchField, setSearchField] = useState(searchFieldParam);
  const debouncedQ = useDebouncedValue(search, 400);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const query = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      sort: sortParam,
      q: debouncedQ || undefined,
      searchField: debouncedQ ? searchField : undefined,
    }),
    [page, sortParam, debouncedQ, searchField]
  );

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await allAPiServicesCall.listArtists(query, getAuthConfig());
      setRows(res?.data || []);
      setPagination(
        res?.pagination || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 }
      );
    } catch (e) {
      console.error(e);
      toast.error("Failed to load artists");
    } finally {
      setLoading(false);
    }
  };

  // Fetch when page/sort/debounced q changes (searchField only matters when there's a query)
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sortParam, debouncedQ, debouncedQ ? searchField : null]);

  // Keep URL in sync when typing (on debounce) or searchField changes
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (debouncedQ) next.set("q", debouncedQ); else next.delete("q");
    next.set("searchField", searchField);
    next.set("page", String(1));             // reset to page 1 on new search
    next.set("sort", sortParam || "-createdAt");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ, searchField]);

  const goToPage = (p) => {
    const next = new URLSearchParams(params);
    next.set("page", String(p));
    setParams(next);
  };

  const setSort = (nextSort) => {
    const next = new URLSearchParams(params);
    next.set("sort", nextSort);
    next.set("page", "1"); // jump back to first page on sort change
    setParams(next);
  };

  const toggleSort = (field) => {
    // current format supports "-field" for desc, "field" for asc
    const isThisField = sortParam.replace("-", "") === field;
    const next = isThisField && !sortParam.startsWith("-")
      ? `-${field}` // if currently asc, switch to desc
      : (isThisField && sortParam.startsWith("-"))
        ? field     // if currently desc, switch to asc
        : `-${field}`; // switching to a new field → start with desc
    setSort(next);
  };

  const sortIcon = (field) => {
    const base = sortParam.replace("-", "");
    if (base !== field) return "↕";
    return sortParam.startsWith("-") ? "▼" : "▲";
  };

  const openDeleteModal = (artist) => {
    setDeleteTarget(artist);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await allAPiServicesCall.deleteArtist(deleteTarget.artist_id, getAuthConfig());
      toast.success("Artist deleted");
      closeDeleteModal();
      if (rows.length === 1 && pagination.page > 1) {
        goToPage(pagination.page - 1);
      } else {
        fetchData();
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const editHref = (id) =>
    ROUTE_CONSTANT.COMMON.EDIT_ARTIST.replace(":id", String(id));

  const clearSearch = () => setSearch("");

  return (
    <CommonLayout>
      <div className="artists-page">
        {/* Header */}
        <div className="artists-header">
          <div className="artists-header-top">
            <div className="artists-title-wrapper">
              <div className="artists-title-icon">
                <FaMicrophone />
              </div>
              <div className="artists-title-text">
                <h1 className="artists-title">Artists</h1>
                <p className="artists-subtitle">Manage your music artists catalog</p>
              </div>
            </div>
            <div className="artists-stats">
              <div className="artists-stat-badge">
                <FaUsers />
                <span>Total: <strong>{pagination.total.toLocaleString()}</strong></span>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Section */}
        <div className="artists-filter-section">
          <div className="artists-filter-header">
            <div className="artists-filter-icon">
              <FaFilter />
            </div>
            <h3 className="artists-filter-title">Filter Artists</h3>
          </div>
          <div className="artists-search-container">
            <div className="artists-search-input-wrapper">
              <FaSearch className="artists-search-input-icon" />
              <input
                type="text"
                className="artists-search-input"
                placeholder={`Search by ${searchFieldOptions.find(o => o.value === searchField)?.label || 'name'}...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button className="artists-search-clear-inline" onClick={clearSearch}>
                  <FaTimes size={12} />
                </button>
              )}
            </div>
            <select
              className="artists-search-select"
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
            >
              {searchFieldOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="artists-filter-hint">
            <FaInfoCircle size={12} />
            <span>Type to search and results will update automatically</span>
          </div>
        </div>

        {/* Main Card */}
        <div className="artists-card">
          {loading ? (
            <div className="artists-loading">
              <div className="artists-loading-spinner"></div>
              <span className="artists-loading-text">Loading artists...</span>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="artists-mobile-view">
                {rows.length === 0 ? (
                  <div className="artists-empty">
                    <FaUsers className="artists-empty-icon" />
                    <span className="artists-empty-text">No artists found.</span>
                  </div>
                ) : (
                  rows.map((row) => (
                    <div key={row.artist_id} className="artists-mobile-card">
                      <div className="artists-mobile-card-header">
                        {row.artist_image_link || row.downloaded_image_link ? (
                          <img
                            src={artistImageUrl(row)}
                            alt={row.artist_name}
                            className="artists-mobile-card-image"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/no-image.png";
                            }}
                          />
                        ) : (
                          <div className="artists-image-placeholder">N/A</div>
                        )}
                        <div className="artists-mobile-card-info">
                          <h3 className="artists-mobile-card-name">{row.artist_name}</h3>
                          <span className="artists-mobile-card-id">#{row.artist_id}</span>
                        </div>
                      </div>
                      <div className="artists-mobile-card-meta">
                        {Array.isArray(row.genres) && row.genres.length > 0 && (
                          <span className="artists-genre-badge">{row.genres[0]}</span>
                        )}
                        {row.createdAt && !isNaN(new Date(row.createdAt).getTime()) && (
                          <span className="artists-date">
                            {new Date(row.createdAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      <div className="artists-mobile-card-actions">
                        <Link
                          to={editHref(row.artist_id)}
                          className="artists-btn artists-btn-edit"
                        >
                          <FaEdit size={14} /> Edit
                        </Link>
                        <button
                          type="button"
                          className="artists-btn artists-btn-delete"
                          onClick={() => openDeleteModal(row)}
                        >
                          <FaTrashAlt size={14} /> Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Desktop Table View */}
              <div className="artists-desktop-view">
                <div className="artists-table-container">
                  <table className="artists-table">
                    <thead>
                      <tr>
                        <th className="sortable" style={{ width: 100 }} onClick={() => toggleSort("artist_id")}>
                          <button className="artists-sort-btn">
                            ID <span className={`artists-sort-icon ${sortParam.includes("artist_id") ? "active" : ""}`}>{sortIcon("artist_id")}</span>
                          </button>
                        </th>
                        <th style={{ width: 80 }}>Image</th>
                        <th className="sortable" style={{ width: '30%' }} onClick={() => toggleSort("artist_name")}>
                          <button className="artists-sort-btn">
                            Name <span className={`artists-sort-icon ${sortParam.includes("artist_name") ? "active" : ""}`}>{sortIcon("artist_name")}</span>
                          </button>
                        </th>
                        <th className="sortable" style={{ width: 150 }} onClick={() => toggleSort("genres")}>
                          <button className="artists-sort-btn">
                            Genres <span className={`artists-sort-icon ${sortParam.includes("genres") ? "active" : ""}`}>{sortIcon("genres")}</span>
                          </button>
                        </th>
                        <th className="sortable" style={{ width: 150 }} onClick={() => toggleSort("createdAt")}>
                          <button className="artists-sort-btn">
                            Created <span className={`artists-sort-icon ${sortParam.includes("createdAt") ? "active" : ""}`}>{sortIcon("createdAt")}</span>
                          </button>
                        </th>
                        <th style={{ width: 160 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr>
                          <td colSpan={6}>
                            <div className="artists-empty">
                              <FaUsers className="artists-empty-icon" />
                              <span className="artists-empty-text">No artists found.</span>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        rows.map((row) => (
                          <tr key={row.artist_id}>
                            <td>
                              <span className="artists-id">#{row.artist_id}</span>
                            </td>
                            <td className="artists-image-cell">
                              {row.artist_image_link || row.downloaded_image_link ? (
                                <Link to={editHref(row.artist_id)} className="artists-image-wrapper">
                                  <img
                                    src={artistImageUrl(row)}
                                    alt={row.artist_name}
                                    className="artists-image"
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = "/no-image.png";
                                    }}
                                  />
                                </Link>
                              ) : (
                                <div className="artists-image-placeholder">N/A</div>
                              )}
                            </td>
                            <td>
                              <Link to={editHref(row.artist_id)} className="artists-name-link">
                                {row.artist_name}
                              </Link>
                            </td>
                            <td>
                              {Array.isArray(row.genres) && row.genres.length > 0 ? (
                                <span className="artists-genre-badge">{row.genres.join(", ")}</span>
                              ) : (
                                <span className="artists-genre-empty">-</span>
                              )}
                            </td>
                            <td>
                              <span className="artists-date">
                                {row.createdAt && !isNaN(new Date(row.createdAt).getTime())
                                  ? new Date(row.createdAt).toLocaleString()
                                  : "-"}
                              </span>
                            </td>
                            <td>
                              <div className="artists-actions">
                                <Link
                                  to={editHref(row.artist_id)}
                                  className="artists-btn artists-btn-edit"
                                >
                                  Edit
                                </Link>
                                <button
                                  type="button"
                                  className="artists-btn artists-btn-delete"
                                  onClick={() => openDeleteModal(row)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {rows.length > 0 && (
                <div className="artists-pagination">
                  <div className="artists-pagination-info">
                    Showing <strong>{(pagination.page - 1) * pagination.limit + 1}</strong> – <strong>{Math.min(pagination.page * pagination.limit, pagination.total)}</strong> of <strong>{pagination.total}</strong> artists
                  </div>
                  <div className="artists-pagination-controls">
                    <button
                      className="artists-pagination-btn"
                      disabled={pagination.page <= 1}
                      onClick={() => goToPage(pagination.page - 1)}
                    >
                      ‹ Prev
                    </button>
                    <span className="artists-pagination-current">
                      {pagination.page} / {pagination.totalPages}
                    </span>
                    <button
                      className="artists-pagination-btn"
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => goToPage(pagination.page + 1)}
                    >
                      Next ›
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        show={showDeleteModal}
        handleClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Artist"
        message={`Are you sure you want to delete "${deleteTarget?.artist_name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleting}
      />
    </CommonLayout>
  );
}